import { useState, useEffect } from 'react';
import { getSupabaseClient } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Activity, AlertTriangle, CheckCircle, XCircle, Clock, Users, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ErrorStats {
  total_errors: number;
  unique_issues: number;
  affected_users: number;
  fatal_errors: number;
  errors: number;
  warnings: number;
  time_range_hours: number;
}

interface HealthCheckData {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  checks: {
    [key: string]: {
      status: 'pass' | 'fail';
      response_time_ms?: number;
      message?: string;
    };
  };
}

export default function MonitoringPage() {
  const [errorStats, setErrorStats] = useState<ErrorStats | null>(null);
  const [healthCheck, setHealthCheck] = useState<HealthCheckData | null>(null);
  const [timeRange, setTimeRange] = useState(24);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMonitoringData();
    const interval = setInterval(loadMonitoringData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [timeRange]);

  const loadMonitoringData = async () => {
    try {
      // Load error statistics
      const client = getSupabaseClient();
      const { data: statsData, error } = await client
        .rpc('get_error_stats', { time_range_hours: timeRange });

      if (error) {
        throw error;
      }

      if (statsData) {
        setErrorStats(statsData);
      }

      // Load health check
      const healthResponse = await fetch('/health-check?detailed=true');
      const healthData = await healthResponse.json();
      setHealthCheck(healthData);

      setLoading(false);
    } catch (error) {
      console.error('Error loading monitoring data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="🔍 System Monitoring" subtitle="Real-time system health and error tracking">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg text-slate-400">Loading monitoring data...</div>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="🔍 System Monitoring"
      subtitle="Real-time system health and error tracking"
      actions={
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(Number(e.target.value))}
          className="px-4 py-2 bg-slate-800 text-slate-200 border border-slate-700 rounded-lg focus:border-blue-500 focus:outline-none"
        >
          <option value={1}>Last Hour</option>
          <option value={24}>Last 24 Hours</option>
          <option value={168}>Last Week</option>
          <option value={720}>Last 30 Days</option>
        </select>
      }
    >
      {/* System Health Status */}
      <div className="bg-slate-900 rounded-lg border border-slate-800 p-6 mb-6">
        <h2 className="text-xl font-semibold text-slate-200 mb-4">System Health</h2>
        {healthCheck ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div
                className={`w-4 h-4 rounded-full ${
                  healthCheck.status === 'healthy'
                    ? 'bg-green-500'
                    : healthCheck.status === 'degraded'
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                }`}
              />
              <span className="text-lg font-medium text-slate-200 capitalize">
                {healthCheck.status}
              </span>
              <span className="text-sm text-slate-400">
                {new Date(healthCheck.timestamp).toLocaleString()}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {Object.entries(healthCheck.checks).map(([name, check]) => (
                <div
                  key={name}
                  className="p-4 bg-slate-800 border border-slate-700 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-200 capitalize">{name}</span>
                    <span
                      className={`text-sm font-semibold ${
                        check.status === 'pass'
                          ? 'text-green-400'
                          : 'text-red-400'
                      }`}
                    >
                      {check.status === 'pass' ? '✓' : '✗'}
                    </span>
                  </div>
                  {check.response_time_ms && (
                    <div className="text-sm text-slate-400">
                      {check.response_time_ms}ms
                    </div>
                  )}
                  {check.message && (
                    <div className="text-sm text-slate-500 mt-1">
                      {check.message}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-slate-400">No health check data available</div>
        )}
      </div>

      {/* Error Statistics */}
      {errorStats && (
        <>
          <h2 className="text-xl font-semibold text-slate-200 mb-4">Error Statistics (Last {timeRange}h)</h2>
          <StatsGrid cols={6}>
            <StatCard
              label="Total Errors"
              value={errorStats.total_errors}
              icon={Activity}
            />
            <StatCard
              label="Unique Issues"
              value={errorStats.unique_issues}
              icon={AlertTriangle}
            />
            <StatCard
              label="Affected Users"
              value={errorStats.affected_users}
              icon={Users}
            />
            <StatCard
              label="Fatal Errors"
              value={errorStats.fatal_errors}
              icon={XCircle}
              variant="critical"
            />
            <StatCard
              label="Errors"
              value={errorStats.errors}
              icon={AlertTriangle}
              variant="warning"
            />
            <StatCard
              label="Warnings"
              value={errorStats.warnings}
              icon={Clock}
            />
          </StatsGrid>
        </>
      )}

      {/* Quick Actions */}
      <div className="bg-slate-900 rounded-lg border border-slate-800 p-6 mt-6">
        <h2 className="text-xl font-semibold text-slate-200 mb-4">Quick Actions</h2>
        <div className="flex gap-4">
          <Link
            to="/errors"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            View All Errors
          </Link>
          <Link
            to="/analytics"
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
          >
            View Analytics
          </Link>
          <Link
            to="/performance"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
          >
            View Performance
          </Link>
        </div>
      </div>
    </AdminPageLayout>
  );
}
